import type { ProjectDocument } from '@bigint-project/cloud-contracts';
import { App as DesktopApp } from '@desktop/App';
import {
  createBrowserAppPlatform,
  createStoreBrowserProjectController,
} from '@desktop/platform/browser';
import type { AppPlatform, TaskCollaborationPort } from '@desktop/platform/ports';
import type {
  BrowserTemplateCreateInput,
  BrowserTemplateExportInput,
} from '@desktop/platform/browser/project-controller';
import type { ProjectTemplate } from '@desktop/persistence/project-template';
import { deserializeProject } from '@desktop/persistence/serialization';
import { captureCurrentViewSnapshot, restoreViewSnapshot } from '@desktop/persistence/view-snapshot';
import { useProjectStore } from '@desktop/store/project-store';
import { useLayoutEffect, useMemo, useRef, type ReactElement } from 'react';

import { toBprojFile } from './file-exchange';
import type { EditorHostProps } from './EditorHost';

type DesktopProjectPlatform = AppPlatform<
  ProjectTemplate,
  BrowserTemplateExportInput,
  BrowserTemplateCreateInput
>;

interface CloudPlatformOptions {
  readonly readOnly: boolean;
  readonly onSave: () => Promise<void>;
  readonly collaboration?: TaskCollaborationPort;
}

/**
 * Keeps local-file open/export behavior in the existing browser controller,
 * while replacing its primary save command with the cloud shell's ETag save.
 */
export function createCloudAppPlatform({
  readOnly,
  onSave,
  collaboration,
}: CloudPlatformOptions): DesktopProjectPlatform {
  const browser = createBrowserAppPlatform({
    // 雲端沒有「未儲存就消失」這回事：編輯持續回寫伺服器，匯入後也還留在
    // 版本歷史裡，所以不對匯入再跳一次本機的未儲存變更確認。
    controller: createStoreBrowserProjectController({ confirm: () => true }),
  });
  const rejectMutation = async (): Promise<boolean> => false;
  const skipMutation = async (): Promise<void> => undefined;

  return {
    ...browser,
    // Cloud create/import always allocates a new server resource in Dashboard;
    // never reinterpret local replacement commands as overwriting this project.
    new: rejectMutation,
    open: rejectMutation,
    save: readOnly ? async () => undefined : onSave,
    createFromTemplate: async () => undefined,
    // 匯出（含範本）沿用 browser adapter 的 Blob 下載；只有會取代專案內容的
    // 匯入要跟著 Viewer 的唯讀政策停用。
    importMspdi: readOnly ? skipMutation : browser.importMspdi,
    importXlsx: readOnly ? skipMutation : browser.importXlsx,
    // Workspace owns the authoritative dirty state and beforeunload warning.
    installLifecycle: () => () => undefined,
    // 留言與附件是雲端專屬能力；桌面與本機瀏覽器宿主不提供這個欄位。
    collaboration,
  };
}

function loadCloudDocument(document: ProjectDocument): void {
  // Reuse the desktop parser so cloud payloads receive the same view defaults
  // and forward-compatible unknown-field handling as a local .bproj file.
  const parsed = deserializeProject(JSON.stringify(toBprojFile(document)));
  useProjectStore.getState().loadProject(parsed.project, null, {
    unknownTopLevelFields: parsed.unknownTopLevelFields,
    unknownViewFields: parsed.unknownViewFields,
  });
  if (parsed.view !== null) restoreViewSnapshot(parsed.view);
}

export function CloudEditorHost({
  document,
  readOnly,
  onChange,
  onViewChange,
  onSave,
  collaboration,
}: EditorHostProps): ReactElement {
  const onChangeRef = useRef(onChange);
  const onViewChangeRef = useRef(onViewChange);
  const readOnlyRef = useRef(readOnly);
  const hydratedSourceRef = useRef<ProjectDocument['project'] | null>(null);
  const emittedProjectRef = useRef<ProjectDocument['project'] | null>(null);
  const hydratingRef = useRef(false);
  onChangeRef.current = onChange;
  onViewChangeRef.current = onViewChange;
  readOnlyRef.current = readOnly;

  const platform = useMemo(
    () => createCloudAppPlatform({ readOnly, onSave, collaboration }),
    [collaboration, onSave, readOnly],
  );

  useLayoutEffect(() => {
    if (hydratedSourceRef.current === document.project) return;
    if (emittedProjectRef.current === document.project) {
      hydratedSourceRef.current = document.project;
      return;
    }
    hydratingRef.current = true;
    try {
      loadCloudDocument(document);
      hydratedSourceRef.current = document.project;
    } finally {
      hydratingRef.current = false;
    }
  }, [document.project]);

  useLayoutEffect(() => {
    return useProjectStore.subscribe((state, previous) => {
      if (hydratingRef.current) return;
      if (state.project !== previous.project) {
        // Browser open/new/template replacement resets the desktop file flag,
        // but in cloud mode it is a pending replacement of the current resource.
        if (!readOnlyRef.current && !state.file.isDirty) useProjectStore.getState().setDirty(true);
        emittedProjectRef.current = state.project;
        onChangeRef.current(state.project);
      }
      const viewChanged =
        state.activeView !== previous.activeView ||
        state.timeScale !== previous.timeScale ||
        state.collapsedTaskIds !== previous.collapsedTaskIds ||
        state.columns !== previous.columns ||
        state.showBaseline !== previous.showBaseline ||
        state.showCriticalPath !== previous.showCriticalPath ||
        state.schedule.mode !== previous.schedule.mode ||
        state.reportPresets !== previous.reportPresets ||
        state.activeReportPresetId !== previous.activeReportPresetId;
      if (viewChanged) onViewChangeRef.current({ ...captureCurrentViewSnapshot() });
    });
  }, []);

  return (
    <section aria-label="排程編輯器">
      {readOnly && (
        <p className="desktop-editor-readonly-note" role="status">
          Viewer 唯讀模式：可切換視圖與檢視排程，所有資料變更控制均已停用。
        </p>
      )}
      <div className="desktop-editor-shell" data-read-only={readOnly ? 'true' : 'false'}>
        <DesktopApp
          platform={platform}
          readOnly={readOnly}
          allowProjectReplacement={false}
          allowProjectRename={document.role === 'owner'}
          showDocumentHeader={false}
        />
      </div>
    </section>
  );
}
